/**
 * Ensemble simulation types & compute-budget helpers.
 *
 * An ensemble runs many simulations from the SAME ignition points, each with
 * small random perturbations on weather / fuel / topography, so the spread
 * across members gives confidence intervals on the outcome.
 */

/**
 * A single perturbed input channel. `magnitudePct` is the 1-sigma Gaussian
 * noise applied to that channel's values, as a percentage.
 */
export interface EnsemblePerturbation {
  enabled: boolean;
  magnitudePct: number;
}

export interface EnsembleConfig {
  enabled: boolean;
  members: number;
  perturbations: {
    weather: EnsemblePerturbation;
    fuel: EnsemblePerturbation;
    topography: EnsemblePerturbation;
  };
}

/**
 * Compute ceiling: a single simulation job can hold at most this many total
 * ignitions (members × ignition points) before it becomes prohibitively
 * expensive. The ensemble size is capped against this.
 */
export const MAX_ENSEMBLE_IGNITIONS = 1000;

/**
 * Largest ensemble size that fits within the compute ceiling for a regime with
 * `ignitionCount` ignition points. Always at least 1. With a single ignition
 * up to MAX_ENSEMBLE_IGNITIONS members are allowed; above ~500 points only one
 * member fits, so an ensemble adds no spread.
 */
export const maxEnsembleMembers = (ignitionCount: number): number =>
  Math.max(1, Math.floor(MAX_ENSEMBLE_IGNITIONS / Math.max(1, ignitionCount)));

/** Sensible default ensemble config, clamped to the regime's member ceiling. */
export const defaultEnsembleConfig = (ignitionCount: number): EnsembleConfig => {
  const maxM = maxEnsembleMembers(ignitionCount);
  return {
    enabled: maxM > 1,
    members: Math.min(50, maxM),
    perturbations: {
      weather: { enabled: true, magnitudePct: 8 },
      fuel: { enabled: true, magnitudePct: 8 },
      topography: { enabled: true, magnitudePct: 8 },
    },
  };
};
